import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { interval } from 'rxjs';
import { DatadogService } from '@app/features/datadog/shared/datadog.service';

/**
 * A signed Datadog embed URL is single-use and expires 30 minutes after it is
 * signed, so a page left open on a wall display would otherwise go stale. Swap in
 * a freshly signed URL comfortably inside that window.
 */
const EMBED_REFRESH_INTERVAL_MS = 25 * 60 * 1000;

@Component({
    selector: 'app-datadog-dashboard',
    templateUrl: './datadog-dashboard.component.html',
    styleUrls: ['./datadog-dashboard.component.css'],
    standalone: false
})
export class DatadogDashboardComponent implements OnInit {

  isLoading: boolean;
  storeNumberInput: number;
  formattedStoreNumber: string;
  iframeUrl: SafeResourceUrl = null;
  embedUnavailable = false;
  private destroyRef = inject(DestroyRef);

  constructor(
    private datadogService: DatadogService,
    private sanitizer: DomSanitizer,
    private activatedRoute: ActivatedRoute
  ) {
    this.activatedRoute.queryParams.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(params => {
      if ('storeNumber' in params) {
        this.formattedStoreNumber = params['storeNumber'];
        this.storeNumberInput = Number(this.formattedStoreNumber);
        this.loadDashboard();
      }
    });

    interval(EMBED_REFRESH_INTERVAL_MS).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      if (this.formattedStoreNumber) {
        this.loadDashboard();
      }
    });
  }

  ngOnInit() {
    // Only fall back to the last searched store when the route didn't name one;
    // the query param is what the iframe was actually loaded with.
    if (!this.formattedStoreNumber) {
      this.formattedStoreNumber = localStorage.getItem('storeNumber');
      if (this.formattedStoreNumber) {
        this.storeNumberInput = Number(this.formattedStoreNumber);
      }
    }
  }

  prependZeros() {
    if (this.storeNumberInput) {
      this.formattedStoreNumber = this.storeNumberInput.toString();
      this.formattedStoreNumber = this.formattedStoreNumber.padStart(5, '0');
    }
    return this.formattedStoreNumber;
  }

  isStoreNumberInvalid() {
    if (this.storeNumberInput) {
      return this.storeNumberInput.toString().length > 5;
    } else {
      return true;
    }
  }

  async loadDashboard() {
    this.prependZeros();
    localStorage.setItem('storeNumber', this.formattedStoreNumber);
    this.isLoading = true;
    this.embedUnavailable = false;

    const embedUrl = await this.datadogService.getSecureEmbedUrlForStore(this.formattedStoreNumber);

    if (embedUrl) {
      this.iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(embedUrl);
    } else {
      this.iframeUrl = null;
      this.embedUnavailable = true;
    }
    this.isLoading = false;
  }
}
